import React, { useEffect, useState, useRef, useCallback } from "react";
import "./TempleHero.css";
import QRCodeStyling from "qr-code-styling";
import Preloader from "../components/Preloader";
import { useParams } from "react-router-dom";
import api from "../services/api";
import { socialLinks, getSocialIcon } from "../config/socialLinks";

interface Props {
  photoBackground: string;
  inviteNom: string;
  jourSemaine: string;
  date: string;
  heure: string;
  dateISO: string;
  heureISO: string;
  invitationImage?: string;
  tableName: string;
  boissonsSansAlcool?: string[];
  boissonsAvecAlcool?: string[];
  numeroMarie: string;
  numeroPicApic: string;
  mapEmbedUrl: string;
  bride: string;
  groom: string;
  theme?: {
    background?: string;
    heart?: string;
    text?: string;
    accent?: string;
    overlay?: string;
    box?: string;
    accentLine?: string;
    inviteBoxBackground?: string;
  };
  couple?: string;
  inviteToken?: string;
  mariageId?: string;
  livreOrActive?: boolean;  
}

interface InviteInfoResponse {
  success: boolean;
  mariageId: string;
  invite?: {
    nom?: string;
    table?: string;
  };
}

interface InvitationInfoResponse {
  success: boolean;
  mariageId: string;
}

interface UpdateResponse {
  success: boolean;
  message?: string;
}

const getUploadedImagePath = (imageName: string): string => {
  if (!imageName || imageName.trim() === '') return '';
  const cleanName = imageName.trim().replace(/\\/g, '/');
  
  if (cleanName.startsWith('http://') || cleanName.startsWith('https://') || cleanName.startsWith('data:')) {
    return cleanName;
  }
  
  const baseUrl = window.location.origin;
  let fileName = cleanName;
  
  if (fileName.startsWith('uploads/')) {
    fileName = fileName.replace('uploads/', '');
  }
  if (fileName.startsWith('/uploads/')) {
    fileName = fileName.replace('/uploads/', '');
  }
  if (fileName.includes('/')) {
    const parts = fileName.split('/');
    fileName = parts[parts.length - 1];
  }
  
  return `${baseUrl}/uploads/${fileName}`;
};

const parseBoissons = (boissons: string[] | undefined): string[] => {
  if (!boissons || !Array.isArray(boissons)) return [];
  return boissons
    .filter(item => item && typeof item === 'string')
    .map(item => item.trim())
    .filter(item => item.length > 0);
};

const extractGoogleMapsSrc = (iframeCode: string): { viewUrl: string } => {
  const srcMatch = iframeCode.match(/src="([^"]+)"/);
  let embedUrl = iframeCode;
  let viewUrl = "https://www.google.com/maps";
  if (srcMatch && srcMatch[1]) {
    embedUrl = srcMatch[1];
    const latMatch = embedUrl.match(/!3d(-?\d+\.\d+)/);
    const lngMatch = embedUrl.match(/!2d(-?\d+\.\d+)/);
    if (latMatch && lngMatch) {
      const lat = latMatch[1];
      const lng = lngMatch[1];
      viewUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    }
  }
  return { viewUrl };
};

const TempleHero: React.FC<Props> = ({
  photoBackground,
  inviteNom,
  jourSemaine,
  date,
  heure,
  dateISO,
  heureISO,
  invitationImage,
  tableName,
  boissonsSansAlcool = [],
  boissonsAvecAlcool = [],
  numeroMarie,
  numeroPicApic,
  mapEmbedUrl,
  bride,
  groom,
  theme = {},
  couple = "non",
  inviteToken: propInviteToken = "",
  mariageId: propMariageId = "",
  livreOrActive = true,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [confirmed, setConfirmed] = useState(false);
  const [selectedBoisson, setSelectedBoisson] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [currentMariageId, setCurrentMariageId] = useState<string>(propMariageId || "");
  const [boissonNotification, setBoissonNotification] = useState<string | null>(null);
  const [countdown, setCountdown] = useState("");
  const [rsvpDeadline, setRsvpDeadline] = useState("");
  const [scrollY, setScrollY] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [interactions, setInteractions] = useState({ aimant: 25, love: 40, content: 20, livre: 15 });
  const [percentages, setPercentages] = useState(interactions);
  
  const params = useParams();
  const urlToken = params.token as string | undefined;
  const inviteToken = urlToken || propInviteToken;
  const hasFetchedMariageId = useRef(false);
  
  const qrRef = useRef<HTMLDivElement>(null);
  const qrCode = useRef<QRCodeStyling | null>(null);
  const [qrInitialized, setQrInitialized] = useState(false);
  const heroRef = useRef<HTMLDivElement>(null);
  const sectionsRef = useRef<(HTMLElement | null)[]>([]);
  const parallaxRef = useRef<HTMLDivElement>(null);

  const colors = {
    background: theme.background || "#faf7f2",
    heartColor: theme.heart || "#c9a87c",
    textPrimary: theme.text || "#4a4a4a",
    textSecondary: theme.text || "#7a7a7a",
    photoOverlay: theme.overlay || "rgba(250, 247, 242, 0.3)",
    boxBackground: theme.box || "#ffffff",
    footerBackground: theme.accent || "#f0e9e0",
    buttonPrimary: theme.heart || "#c9a87c",
    accentLine: theme.accentLine || "#c9a87c",
    inviteBoxBackground: theme.inviteBoxBackground || "#ffffff",
  };

  const cleanedBoissonsSansAlcool = parseBoissons(boissonsSansAlcool);
  const cleanedBoissonsAvecAlcool = parseBoissons(boissonsAvecAlcool);
  const { viewUrl: googleMapsViewUrl } = extractGoogleMapsSrc(mapEmbedUrl);

  const setSectionRef = (index: number) => (el: HTMLElement | null) => {
    sectionsRef.current[index] = el;
  };

  useEffect(() => {
    const oldMeta = document.querySelectorAll('meta[property^="og:"]');
    oldMeta.forEach(meta => meta.remove());
    
    const meta = document.createElement('meta');
    meta.setAttribute('property', 'og:title');
    meta.setAttribute('content', `Mariage de ${bride} & ${groom}`);
    document.head.appendChild(meta);
    
    const meta2 = document.createElement('meta');
    meta2.setAttribute('property', 'og:description');
    meta2.setAttribute('content', 'Nous avons l\'honneur de vous inviter à célébrer notre mariage');
    document.head.appendChild(meta2);
    
    const meta3 = document.createElement('meta');
    meta3.setAttribute('property', 'og:image');
    meta3.setAttribute('content', getUploadedImagePath(invitationImage || photoBackground));
    document.head.appendChild(meta3);
    
    const meta4 = document.createElement('meta');
    meta4.setAttribute('property', 'og:url');
    meta4.setAttribute('content', window.location.href);
    document.head.appendChild(meta4);
    
    const meta5 = document.createElement('meta');
    meta5.setAttribute('property', 'og:type');
    meta5.setAttribute('content', 'website');
    document.head.appendChild(meta5);
    
    return () => {
      const metas = document.querySelectorAll('meta[property^="og:"]');
      metas.forEach(meta => meta.remove());
    };
  }, [bride, groom, invitationImage, photoBackground]);

  useEffect(() => {
    if (hasFetchedMariageId.current) return;
    
    const fetchMariageId = async () => {
      hasFetchedMariageId.current = true;
      if (propMariageId && propMariageId.trim() && propMariageId !== 'unknown') {
        setCurrentMariageId(propMariageId);
        return;
      }
      if (!inviteToken) {
        setCurrentMariageId('unknown');
        return;
      }
      try {
        const savedData = localStorage.getItem(`invite_${inviteToken}`);
        if (savedData) {
          const parsedData = JSON.parse(savedData);
          if (parsedData.mariageId && parsedData.mariageId !== 'unknown') {
            setCurrentMariageId(parsedData.mariageId);
            return;
          }
        }
      } catch (error) {}
      try {
        const response = await api.get<InviteInfoResponse>(`/invites/info/${inviteToken}`);
        if (response.data.success && response.data.mariageId) {
          setCurrentMariageId(response.data.mariageId);
          localStorage.setItem(`invite_${inviteToken}`, JSON.stringify({
            mariageId: response.data.mariageId,
            token: inviteToken,
            nom: response.data.invite?.nom || inviteNom,
            table: response.data.invite?.table || tableName,
            timestamp: new Date().toISOString()
          }));
          return;
        }
        const fallbackResponse = await api.get<InvitationInfoResponse>(`/invitation-info/${inviteToken}`);
        if (fallbackResponse.data.success && fallbackResponse.data.mariageId) {
          setCurrentMariageId(fallbackResponse.data.mariageId);
          return; 
        }
        setCurrentMariageId('unknown');
      } catch (error) {
        setCurrentMariageId('unknown');
      }
    };
    fetchMariageId();
  }, [propMariageId, inviteToken, inviteNom, tableName]);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
      
      const sections = sectionsRef.current;
      for (let i = 0; i < sections.length; i++) {
        const section = sections[i];
        if (section) {
          const rect = section.getBoundingClientRect();
          if (rect.top <= window.innerHeight/2 && rect.bottom >= window.innerHeight/2) {
            setActiveIndex(i);
            break;
          }
        }
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      setMousePos({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 20
      });
    };
    
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("mousemove", handleMouseMove);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  const saveChoicesToBackend = useCallback(async (statut: string, boissonPreferee?: string) => {
    if (!inviteToken) return;

    const localData = {
      statut,
      boissonPreferee: boissonPreferee || null,
      mariageId: currentMariageId || 'unknown',
      timestamp: new Date().toISOString()
    };
    
    localStorage.setItem(`invite_${inviteToken}`, JSON.stringify(localData));
    setSaveMessage(statut === 'confirmed' ? "✓ Présence confirmée" : "✓ Annulé");
    setTimeout(() => setSaveMessage(null), 2000);
  }, [currentMariageId, inviteToken]);

  const handleConfirmPresence = () => {
    const newConfirmedState = !confirmed;
    setConfirmed(newConfirmedState);
    saveChoicesToBackend(newConfirmedState ? 'confirmed' : 'pending');
  };

  const handleSelectBoisson = (boisson: string) => {
    if (!confirmed) {
      setSaveMessage("⚠️ Veuillez d'abord confirmer votre présence pour choisir une boisson.");
      setTimeout(() => setSaveMessage(null), 2000);
      return;
    }
    
    const newBoisson = selectedBoisson === boisson ? null : boisson;
    setSelectedBoisson(newBoisson);
    if (newBoisson) {
      setBoissonNotification(`✓ ${newBoisson}`);
      setTimeout(() => setBoissonNotification(null), 1500);
    }
  };

  const handleClickInteraction = (type: "aimant" | "love" | "content" | "livre") => {
    setInteractions((prev) => {
      const updated = { ...prev, [type]: prev[type] + 1 };
      const total = updated.aimant + updated.love + updated.content + updated.livre;
      setPercentages({
        aimant: Math.round((updated.aimant / total) * 100),
        love: Math.round((updated.love / total) * 100),
        content: Math.round((updated.content / total) * 100),
        livre: Math.round((updated.livre / total) * 100),
      });
      return updated;
    });
  };

  useEffect(() => {
    if (inviteToken) {
      const savedData = localStorage.getItem(`invite_${inviteToken}`);
      if (savedData) {
        try {
          const parsedData = JSON.parse(savedData);
          setConfirmed(parsedData.statut === 'confirmed');
          setSelectedBoisson(parsedData.boissonPreferee || null);
        } catch (error) {}
      }
    }
  }, [inviteToken]);

  // Génération initiale du QR code
  useEffect(() => {
    if (!isLoading && !qrInitialized && qrRef.current) {
      qrCode.current = new QRCodeStyling({
        width: 180,
        height: 180,
        data: inviteToken || `${inviteNom}\n${bride} & ${groom}`,
        margin: 5,
        dotsOptions: { color: colors.heartColor, type: "rounded" },
        backgroundOptions: { color: "#ffffff" },
      });

      qrRef.current.innerHTML = "";
      qrCode.current.append(qrRef.current);
      setQrInitialized(true);
    }
  }, [isLoading, qrInitialized, colors.heartColor, inviteToken, inviteNom, bride, groom]);

  // Force la régénération du QR code quand la confirmation change
  useEffect(() => {
    if (confirmed && qrRef.current) {
      const qrData = inviteToken || `${inviteNom}\n${bride} & ${groom}`;
      
      if (qrCode.current) {
        qrCode.current.update({ data: qrData });
      } else {
        qrCode.current = new QRCodeStyling({
          width: 180,
          height: 180,
          data: qrData,
          margin: 5,
          dotsOptions: { color: colors.heartColor, type: "rounded" },
          backgroundOptions: { color: "#ffffff" },
        });
        
        if (qrRef.current) {
          qrRef.current.innerHTML = "";
          qrCode.current.append(qrRef.current);
        }
      }
    }
  }, [confirmed, colors.heartColor, inviteToken, inviteNom, bride, groom]);

  const handleDownload = () => {
    if (!confirmed) {
      setSaveMessage("⚠️ Veuillez d'abord confirmer votre présence pour télécharger votre QR code.");
      setTimeout(() => setSaveMessage(null), 2000);
      return;
    }
    qrCode.current?.download({ name: `qr-${inviteNom}`, extension: "png" });
  };

  useEffect(() => {
    const eventDate = new Date(`${dateISO}T${heureISO}`);
    if (isNaN(eventDate.getTime())) return;
    
    const updateCountdown = () => {
      const diff = eventDate.getTime() - new Date().getTime();
      if (diff <= 0) {
        setCountdown("Aujourd'hui");
        return;
      }
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
      setCountdown(`${days}j ${hours}h`);
    };
    
    updateCountdown();
    const interval = setInterval(updateCountdown, 60000);
    
    const deadline = new Date(eventDate);
    deadline.setDate(deadline.getDate() - 12);
    setRsvpDeadline(deadline.toLocaleDateString("fr-FR", { day: "numeric", month: "long" }));
    
    return () => clearInterval(interval);
  }, [dateISO, heureISO]);

  if (isLoading) {
    return <Preloader 
      bride={bride}
      groom={groom}
      colors={{
        background: colors.background,
        heart: colors.heartColor,
        text: colors.textPrimary,
        accent: colors.buttonPrimary
      }}
      durationMs={800}
      onFinish={() => setIsLoading(false)}
    />;
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600&family=Montserrat:wght@300;400;500&display=swap');

        .notification {
          position: fixed;
          top: 20px;
          right: 20px;
          padding: 12px 24px;
          background: ${colors.heartColor};
          color: #ffffff;
          z-index: 2000;
          font-family: 'Montserrat', sans-serif;
          font-size: 14px;
          border-radius: 30px;
          box-shadow: 0 10px 30px rgba(0,0,0,0.1);
          animation: slideIn 0.3s ease;
        }

        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }

        .progress-dots {
          position: fixed;
          right: 30px;
          top: 50%;
          transform: translateY(-50%);
          z-index: 100;
          display: flex;
          flex-direction: column;
          gap: 15px;
        }

        .progress-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background: ${colors.textSecondary};
          opacity: 0.3;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .progress-dot.active {
          opacity: 1;
          background: ${colors.heartColor};
          transform: scale(1.3);
        }
      `}</style>

      {saveMessage && <div className="notification">{saveMessage}</div>}
      {boissonNotification && <div className="notification">{boissonNotification}</div>}

      <div className="temple-hero" ref={heroRef} style={{ background: colors.background }}>
        
        <div className="progress-dots">
          {[0,1,2,3,4,5,6,7,8,9].map(i => (
            <div 
              key={i}
              className={`progress-dot ${activeIndex === i ? 'active' : ''}`}
              onClick={() => sectionsRef.current[i]?.scrollIntoView({ behavior: 'smooth' })}
            />
          ))}
        </div>

        <section ref={setSectionRef(0)} className="hero-section">
          <div 
            className="hero-parallax"
            ref={parallaxRef}
            style={{ 
              transform: `translate3d(${mousePos.x}px, ${scrollY * 0.3 + mousePos.y}px, 0) scale(1.05)`,
            }}
          >
            <img src={getUploadedImagePath(photoBackground)} alt="" className="hero-image" />
            <div className="hero-overlay" style={{ background: colors.photoOverlay }}></div>
          </div>
          
          <div className="hero-content" style={{ transform: `translateY(${scrollY * 0.1}px)` }}>
            <div className="hero-symbol" style={{ color: colors.heartColor }}>⚭</div>
            <h1 className="hero-title">
              <span className="hero-bride" style={{ color: colors.textPrimary }}>{bride}</span>
              <span className="hero-ampersand" style={{ color: colors.heartColor }}>&</span>
              <span className="hero-groom" style={{ color: colors.textPrimary }}>{groom}</span>
            </h1>
            <div className="hero-badge" style={{ borderColor: colors.heartColor }}>
              <span style={{ color: colors.heartColor }}>{inviteNom}</span>
            </div>
            <div className="hero-datetime">
              <span className="hero-day" style={{ color: colors.textSecondary }}>{jourSemaine}</span>
              <span className="hero-date" style={{ color: colors.heartColor }}>{date}</span>
              <span className="hero-time" style={{ color: colors.textSecondary }}>{heure}</span>
            </div>
          </div>
        </section>

        <section ref={setSectionRef(1)} className="content-section">
          <div className="container">
            <div className="elegant-card" style={{ background: colors.inviteBoxBackground }}>
              <div className="card-corner top-left" style={{ borderColor: colors.heartColor }}></div>
              <div className="card-corner top-right" style={{ borderColor: colors.heartColor }}></div>
              <h2 className="section-title" style={{ color: colors.heartColor }}>Invitation</h2>
              <p className="guest-name" style={{ color: colors.textPrimary }}>{inviteNom}</p>
              <div className="divider" style={{ background: colors.heartColor }}></div>
              <p className="invitation-text" style={{ color: colors.textSecondary }}>
                Nous avons le plaisir de vous convier à célébrer notre union.
              </p>
              <div className="countdown-chip" style={{ background: colors.heartColor }}>
                <span className="countdown-label">J-</span>
                <span className="countdown-number">{countdown}</span>
              </div>
              <div className="card-corner bottom-left" style={{ borderColor: colors.heartColor }}></div>
              <div className="card-corner bottom-right" style={{ borderColor: colors.heartColor }}></div>
            </div>
          </div>
        </section>

        {invitationImage && (
          <section ref={setSectionRef(2)} className="content-section">
            <div className="container">
              <div className="image-frame" style={{ borderColor: colors.heartColor }}>
                <img src={getUploadedImagePath(invitationImage)} alt="Invitation" className="featured-image" />
              </div>
            </div>
          </section>
        )}

        <section ref={setSectionRef(3)} className="content-section">
          <div className="container">
            <h2 className="section-title" style={{ color: colors.heartColor }}>Confirmation</h2>
            <p className="section-subtitle" style={{ color: colors.textSecondary }}>
              Avant le {rsvpDeadline}
            </p>
            
            <button 
              className={`rsvp-button ${confirmed ? 'confirmed' : ''}`}
              style={{ 
                background: confirmed ? 'transparent' : colors.heartColor,
                color: confirmed ? colors.heartColor : '#ffffff',
                borderColor: colors.heartColor
              }}
              onClick={handleConfirmPresence}
            >
              {confirmed ? '✓ Présence confirmée' : 'Confirmer ma présence'}
            </button>
          </div>
        </section>

        <section ref={setSectionRef(4)} className="content-section">
          <div className="container">
            <h2 className="section-title" style={{ color: colors.heartColor }}>QR Code</h2>
            {!confirmed ? (
              <div className="qr-disabled-message" style={{ 
                textAlign: 'center', 
                padding: '30px',
                background: `${colors.heartColor}10`,
                borderRadius: '16px',
                margin: '20px 0'
              }}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginBottom: '15px' }}>
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill={colors.heartColor}/>
                </svg>
                <p style={{ color: colors.textPrimary, marginBottom: '15px' }}>
                  Veuillez d'abord confirmer votre présence pour accéder à votre QR code.
                </p>
                <button 
                  onClick={() => document.querySelector('.rsvp-button')?.scrollIntoView({ behavior: 'smooth' })}
                  style={{
                    background: colors.heartColor,
                    color: '#fff',
                    border: 'none',
                    padding: '10px 25px',
                    borderRadius: '30px',
                    cursor: 'pointer'
                  }}
                >
                  Confirmer ma présence
                </button>
              </div>
            ) : (
              <>
                <div className="qr-wrapper" ref={qrRef}></div>
                <button className="qr-button" style={{ color: colors.heartColor }} onClick={handleDownload}>
                  Télécharger
                </button>
              </>
            )}
          </div>
        </section>

        {(cleanedBoissonsSansAlcool.length > 0 || cleanedBoissonsAvecAlcool.length > 0) && (
          <section ref={setSectionRef(5)} className="content-section">
            <div className="container">
              <h2 className="section-title" style={{ color: colors.heartColor }}>Boissons</h2>
              
              {!confirmed ? (
                <div className="boissons-disabled-message" style={{ 
                  textAlign: 'center', 
                  padding: '30px',
                  background: `${colors.heartColor}10`,
                  borderRadius: '16px'
                }}>
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginBottom: '15px' }}>
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill={colors.heartColor}/>
                  </svg>
                  <p style={{ color: colors.textPrimary, marginBottom: '15px' }}>
                    Veuillez d'abord confirmer votre présence pour choisir votre boisson.
                  </p>
                  <button 
                    onClick={() => document.querySelector('.rsvp-button')?.scrollIntoView({ behavior: 'smooth' })}
                    style={{
                      background: colors.heartColor,
                      color: '#fff',
                      border: 'none',
                      padding: '10px 25px',
                      borderRadius: '30px',
                      cursor: 'pointer'
                    }}
                  >
                    Confirmer ma présence
                  </button>
                </div>
              ) : (
                <div className="drinks-container">
                  {cleanedBoissonsSansAlcool.length > 0 && (
                    <div className="drinks-category">
                      <h3 style={{ color: colors.heartColor }}>Sans alcool</h3>
                      <div className="drinks-grid">
                        {cleanedBoissonsSansAlcool.map((item, i) => (
                          <button
                            key={i}
                            className={`drink-item ${selectedBoisson === item ? 'selected' : ''}`}
                            style={{ 
                              background: selectedBoisson === item ? colors.heartColor : 'transparent',
                              color: selectedBoisson === item ? '#ffffff' : colors.textPrimary,
                              borderColor: colors.heartColor
                            }}
                            onClick={() => handleSelectBoisson(item)}
                          >
                            {item}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {cleanedBoissonsAvecAlcool.length > 0 && (
                    <div className="drinks-category">
                      <h3 style={{ color: colors.heartColor }}>Avec alcool</h3>
                      <div className="drinks-grid">
                        {cleanedBoissonsAvecAlcool.map((item, i) => (
                          <button
                            key={i}
                            className={`drink-item ${selectedBoisson === item ? 'selected' : ''}`}
                            style={{ 
                              background: selectedBoisson === item ? colors.heartColor : 'transparent',
                              color: selectedBoisson === item ? '#ffffff' : colors.textPrimary,
                              borderColor: colors.heartColor
                            }}
                            onClick={() => handleSelectBoisson(item)}
                          >
                            {item}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>
        )}

        {/* livre d'or */}
        {livreOrActive && (
          <section ref={setSectionRef(6)} className="content-section">
            <div className="container">
              <h2 className="section-title" style={{ color: colors.heartColor }}>Message</h2>
              
              <form className="message-form" onSubmit={(e) => {
                e.preventDefault();
                const msg = (e.currentTarget.elements.namedItem('message') as HTMLTextAreaElement).value;
                
                const messageComplet = `LIVRE D'OR

De la part de : ${inviteNom}
Message : "${msg}"

Merci infiniment pour votre message. Nous vous sommes très reconnaissants.`;
                
                window.open(`https://wa.me/${numeroMarie}?text=${encodeURIComponent(messageComplet)}`, "_blank");
                
                alert("✅ Message envoyé avec succès !");
                e.currentTarget.reset();
              }}>
                <textarea 
                  name="message" 
                  placeholder="Votre message..."
                  className="message-input"
                  style={{ borderColor: colors.heartColor }}
                  required
                />
                <button 
                  type="submit" 
                  className="message-submit"
                  style={{ background: colors.heartColor, color: '#ffffff' }}
                >
                  Envoyer
                </button>
              </form>
            </div>
          </section>
        )}

        <section ref={setSectionRef(7)} className="content-section">
          <div className="container">
            <h2 className="section-title" style={{ color: colors.heartColor }}>Votre avis</h2>
            
            <div className="reactions-container">
              <div className="reactions-buttons">
                {[
                  { type: 'aimant', emoji: '👌' },
                  { type: 'love', emoji: '❤️' },
                  { type: 'content', emoji: '😁' },
                  { type: 'livre', emoji: '🤭' }
                ].map((btn) => (
                  <button 
                    key={btn.type}
                    className="reaction-btn" 
                    style={{ backgroundColor: colors.heartColor }}
                    onClick={() => handleClickInteraction(btn.type as any)}
                  >
                    {btn.emoji}
                  </button>
                ))}
              </div>
              
              <div className="reactions-stats">
                <div className="stat-item">
                  <span className="stat-label" style={{ color: colors.textPrimary }}>👌 Super</span>
                  <div className="stat-progress">
                    <div className="stat-progress-fill" style={{ width: `${percentages.aimant}%`, backgroundColor: colors.heartColor }}></div>
                  </div>
                  <span className="stat-value" style={{ color: colors.heartColor }}>{percentages.aimant}%</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label" style={{ color: colors.textPrimary }}>❤️ Love</span>
                  <div className="stat-progress">
                    <div className="stat-progress-fill" style={{ width: `${percentages.love}%`, backgroundColor: colors.heartColor }}></div>
                  </div>
                  <span className="stat-value" style={{ color: colors.heartColor }}>{percentages.love}%</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label" style={{ color: colors.textPrimary }}>😁 Content</span>
                  <div className="stat-progress">
                    <div className="stat-progress-fill" style={{ width: `${percentages.content}%`, backgroundColor: colors.heartColor }}></div>
                  </div>
                  <span className="stat-value" style={{ color: colors.heartColor }}>{percentages.content}%</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label" style={{ color: colors.textPrimary }}>🤭 Mignon</span>
                  <div className="stat-progress">
                    <div className="stat-progress-fill" style={{ width: `${percentages.livre}%`, backgroundColor: colors.heartColor }}></div>
                  </div>
                  <span className="stat-value" style={{ color: colors.heartColor }}>{percentages.livre}%</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section ref={setSectionRef(8)} className="content-section">
          <div className="container">
            <h2 className="section-title" style={{ color: colors.heartColor }}>Lieu</h2>
            
            <a href={googleMapsViewUrl} target="_blank" rel="noopener noreferrer" className="map-link">
              <img src="/assets/elegant/carte.png" alt="Carte" className="map-image" />
              <span className="map-button" style={{ color: colors.heartColor }}>Voir l'itinéraire →</span>
            </a>
          </div>
        </section>

        <section ref={setSectionRef(9)} className="content-section">
          <div className="container">
            <h2 className="section-title" style={{ color: colors.heartColor }}>Contact</h2>
            
            <a 
              href={`https://wa.me/${numeroPicApic}`}
              target="_blank"
              rel="noopener noreferrer"
              className="contact-button"
              style={{ background: colors.heartColor, color: '#ffffff' }}
            >
              WhatsApp
            </a>
          </div>
        </section>

        <footer className="footer" style={{ background: colors.footerBackground }}>
          <div className="footer-content">
            <p className="footer-couple" style={{ color: colors.textPrimary }}>
              {bride} & {groom}
            </p>
            <p className="footer-date" style={{ color: colors.heartColor }}>
              {jourSemaine} {date}
            </p>
            <div className="footer-social">
              <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" style={{ color: colors.heartColor }}>
                {getSocialIcon('instagram', colors.heartColor, 22)}
              </a>
              <a href={socialLinks.tiktok} target="_blank" rel="noopener noreferrer" style={{ color: colors.heartColor }}>
                {getSocialIcon('tiktok', colors.heartColor, 22)}
              </a>
              <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer" style={{ color: colors.heartColor }}>
                {getSocialIcon('facebook', colors.heartColor, 22)}
              </a>
            </div>
            <p className="footer-credits" style={{ color: colors.textSecondary }}>
              <a href={socialLinks.picapic} target="_blank" rel="noopener noreferrer" style={{ color: colors.textSecondary, textDecoration: 'none' }}>
                PicApic
              </a>
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default TempleHero;